import {
  applyMatrix3,
  WHITE_POINTS,
  type Illuminant,
  type LinearRGB,
  type Matrix3,
  type XYZ,
} from './types.js';

/**
 * Linear sRGB -> XYZ (D65).
 *
 * Derived from the IEC 61966-2-1 primaries adapted to the D65 white point in
 * `WHITE_POINTS`, so that linear (1,1,1) maps onto that white point exactly.
 * Regenerate both matrices together if the white point ever changes.
 */
export const LINEAR_SRGB_TO_XYZ_D65: Matrix3 = [
  0.41245643908969221, 0.35757607764390897, 0.18043748326639891,
  0.21267285140562253, 0.71515215528781795, 0.072174993306559562,
  0.0193338955823293, 0.11919202588130297, 0.95030407853636767,
];

/** XYZ (D65) -> linear sRGB. Exact numerical inverse of the matrix above. */
export const XYZ_D65_TO_LINEAR_SRGB: Matrix3 = [
  3.2404541621141050, -1.5371385127977166, -0.49853140955601605,
  -0.96926603050518689, 1.8760108454466944, 0.041556017530349841,
  0.055643430959114761, -0.20402591351675387, 1.0572251882231791,
];

/** Bradford cone response matrix, used for chromatic adaptation. */
const BRADFORD: Matrix3 = [
  0.8951, 0.2664, -0.1614,
  -0.7502, 1.7135, 0.0367,
  0.0389, -0.0685, 1.0296,
];

/**
 * Exact numerical inverse of BRADFORD. The 7-decimal figures usually quoted for
 * this matrix are not a true inverse of the forward one — round-tripping
 * through them leaves a residual around 4e-9, which puts a needless accuracy
 * floor under every D50 conversion.
 */
const BRADFORD_INVERSE: Matrix3 = [
  0.98699290546671214, -0.14705425642099013, 0.15996265166373122,
  0.43230526972339450, 0.51836027153677744, 0.049291228212855594,
  -0.0085286645751773207, 0.040042821654084862, 0.96848669578754998,
];

export function linearRgbToXyz(c: LinearRGB): XYZ {
  const [x, y, z] = applyMatrix3(LINEAR_SRGB_TO_XYZ_D65, [c.r, c.g, c.b]);
  return { x, y, z };
}

export function xyzToLinearRgb(c: XYZ): LinearRGB {
  const [r, g, b] = applyMatrix3(XYZ_D65_TO_LINEAR_SRGB, [c.x, c.y, c.z]);
  return { r, g, b };
}

/**
 * Bradford chromatic adaptation between two illuminants.
 *
 * Paint and coatings specs are usually published under D50, while camera
 * output is sRGB/D65 — this is the bridge between the two. A no-op when
 * `from` and `to` match.
 */
export function adaptXyz(c: XYZ, from: Illuminant, to: Illuminant): XYZ {
  if (from === to) return c;

  const src = WHITE_POINTS[from];
  const dst = WHITE_POINTS[to];
  const [srcL, srcM, srcS] = applyMatrix3(BRADFORD, [src.x, src.y, src.z]);
  const [dstL, dstM, dstS] = applyMatrix3(BRADFORD, [dst.x, dst.y, dst.z]);

  const [l, m, s] = applyMatrix3(BRADFORD, [c.x, c.y, c.z]);
  const adapted: [number, number, number] = [
    (l * dstL) / srcL,
    (m * dstM) / srcM,
    (s * dstS) / srcS,
  ];

  const [x, y, z] = applyMatrix3(BRADFORD_INVERSE, adapted);
  return { x, y, z };
}
